import Axios from 'axios';
import { loadTossPayments } from '@tosspayments/tosspayments-sdk';
import { v4 as uuidv4 } from 'uuid';

import { PAYMENT_METHOD, PAY_TYPE } from '@/constants';
import { CreateOrderParameters, api, PaymentMethod } from '@/apis';

type AdditionalParams = {
  cardCompany?: string | null;
  easyPay?: string | null;
  flowMode?: 'DIRECT' | 'DEFAULT';
  cardInstallmentPlan?: number;
  useCardPoint?: boolean;
  card?: {
    useEscrow?: boolean;
    flowMode?: 'DIRECT' | 'DEFAULT';
    cardCompany?: string;
    cardInstallmentPlan?: number;
    useCardPoint?: boolean;
    useAppCardOnly?: boolean;
    easyPay?: string;
  };
  virtualAccount?: {
    useEscrow?: boolean;
    cashReceipt?: {
      type: '소득공제' | '지출증빙' | '미발행';
    };
    validHours?: number;
  };
};

type PaymentConfig = Record<
  PaymentMethod,
  {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    method: any;
    additionalParams?: Partial<AdditionalParams>;
    easyPay?: string;
  }
>;

export const createOrder = async (
  parameters: CreateOrderParameters,
  cardInfo: {
    cardCompany: string;
    cardInstallmentPlan: string;
  },
  version?: string,
  key?: string,
) => {
  try {
    const { data, status } = await api.orders.check(parameters, version);
    if (status !== 200) throw new Error('주문 확인 실패 🥲');

    const paymentConfig: PaymentConfig = {
      [PAYMENT_METHOD.VIRTUAL_ACCOUNT]: {
        method: 'VIRTUAL_ACCOUNT',
        additionalParams: {
          virtualAccount: {
            useEscrow: false,
            cashReceipt: { type: '소득공제' },
            validHours: 1,
          },
        },
      },
      [PAYMENT_METHOD.CARD]: {
        method: 'CARD',
        additionalParams: {
          card: {
            useEscrow: false,
            flowMode: 'DIRECT',
            cardCompany: cardInfo.cardCompany,
            cardInstallmentPlan: Number(cardInfo.cardInstallmentPlan) ?? 0,
            useCardPoint: false,
          },
        },
      },
      [PAYMENT_METHOD.TOSS_PAY]: {
        method: 'CARD',
        additionalParams: {
          card: {
            useEscrow: false,
            easyPay: 'TOSSPAY',
            flowMode: 'DIRECT',
          },
        },
      },
      [PAYMENT_METHOD.NAVER_PAY]: {
        method: 'CARD',
        additionalParams: {
          card: {
            useEscrow: false,
            flowMode: 'DIRECT',
            easyPay: 'NAVERPAY',
          },
        },
      },
      [PAYMENT_METHOD.KAKAO_PAY]: {
        method: 'CARD',
        additionalParams: {
          card: {
            useEscrow: false,
            flowMode: 'DIRECT',
            easyPay: 'KAKAOPAY',
          },
        },
      },
    };

    const config = paymentConfig[parameters.paymentMethod];
    if (!config) throw new Error('지원하지 않는 결제 방식입니다.');
    const { method, additionalParams = {} } = config;

    const CALLBACK_URL = version
      ? `${window.location.origin}/pay/callback?version=${version}`
      : `${window.location.origin}/pay/callback`;

    const tossPayments = await loadTossPayments(
      key ?? (process.env.REACT_APP_TOSS_CLIENT_KEY as string),
    );

    const commonPayload = {
      method,
      amount: {
        currency: 'KRW',
        value: data.total_price,
      },
      orderId: data.order_number,
      taxFreeAmount: parameters.tax_free_price,
      orderName: data.order_name,
      successUrl: `${CALLBACK_URL}`,
      failUrl: `${CALLBACK_URL}/fail`,
      customerEmail: parameters.buyer_email,
      customerName: parameters.buyer_name,
    };

    const payment = tossPayments.payment({
      customerKey: uuidv4(),
    });
    return await payment.requestPayment({
      ...commonPayload, // 공통 payload
      ...additionalParams, // 결제 수단에 따른 payload
    });
  } catch (error) {
    if (Axios.isAxiosError(error)) {
      throw error;
    } else if (error instanceof Error) {
      throw error.message;
    } else {
      throw '결제에 실패했어요 ☺️';
    }
  }
};

/**
 * 결제 수단을 받아서 카드인 경우 카드(카드사/할부개월수), 간편결제의 경우 결제수단/할부개월수, 그 외의 경우는 결제 수단을 반환합니다
 * @param payment_info 결제 수단 (pay_type, card_issuer, installment_plan_months)
 * @returns 포맷된 결제 수단
 */
export const formatPayType = (payment_info: {
  pay_type: keyof typeof PAY_TYPE;
  card_issuer: string | null;
  installment_plan_months: number | null;
}) => {
  const { pay_type, card_issuer, installment_plan_months } = payment_info;
  if (pay_type === 'cash') return PAY_TYPE[pay_type]; // 가상 계좌
  if (card_issuer === null || installment_plan_months === null) return PAY_TYPE[pay_type];
  return `${PAY_TYPE[pay_type]}(${card_issuer}/${
    installment_plan_months === 0 ? '일시불' : `${installment_plan_months}개월`
  })`;
};
